import { useTranslation } from "react-i18next";

import { ProductCardChip } from "@/components/Layout/BaseLayouts/ProductCard/ProductCardChip";
import {
  ProductCardFooterItem,
  ProductCardListStep,
} from "@/components/Layout/BaseLayouts/ProductCard/ProductCardFooterItem";
import { DeviceProductCard } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/DeviceProductCard.tsx";
import { getIconForProductCategory } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/ProductCategoryIcon.tsx";
import { ExtendedSimCardDetail } from "@/hooks/useExtendedSims.ts";
import { useGetTitle } from "@/hooks/useGetTitle.ts";
import { portalRoutes } from "@/router/routes/portalRoutes.ts";
import { getLocalizedDateString } from "@/utils/dateHelpers";
import { getFlatNameFromDevice } from "@/utils/deviceUtils";

interface NumberPortingTileProps {
  device: ExtendedSimCardDetail;
  missingInfo?: boolean;
  manageRoute: string;
}

export const NumberPortingTile = ({
  device,
  missingInfo,
  manageRoute,
}: NumberPortingTileProps) => {
  const { t, i18n } = useTranslation("portal");
  const { t: tDefault } = useTranslation();

  const icon = getIconForProductCategory(device);
  const title = useGetTitle(device.alias);

  const numberPorting = device.numberPorting;

  const requestedTitle = t("layout.tiles.numberPorting.sections.requested");
  const dateKnownTitle = numberPorting?.date
    ? t("layout.tiles.numberPorting.sections.known", {
        val: getLocalizedDateString(numberPorting.date, i18n.language),
      })
    : t("layout.tiles.numberPorting.sections.dateBeingIdentified");
  const doneTitle = t("layout.tiles.numberPorting.sections.done");
  const missingInformationTitle = t(
    "layout.tiles.numberPorting.sections.missingInformation",
  );
  const simActivationPendingTitle = t(
    "layout.tiles.numberPorting.sections.simActivationPending",
  );

  const sectionTitles = missingInfo
    ? [missingInformationTitle, simActivationPendingTitle]
    : [requestedTitle, dateKnownTitle, doneTitle];

  const step =
    ["requested", "dateKnown"].indexOf(numberPorting?.status || "") + 1;
  // Hardcoded step for missingInfo, because it can't have another state
  const currentStep = missingInfo ? 1 : step;

  const steps: ProductCardListStep[] = sectionTitles.map((label, index) => ({
    label,
    status:
      index + 1 < currentStep
        ? "done"
        : index + 1 === currentStep
          ? "in-progress"
          : "to-be-done",
  }));

  const isTransferring = !missingInfo && numberPorting?.status === "dateKnown";

  return (
    <DeviceProductCard
      dataTestid="number-porting-tile"
      icon={icon}
      title={title}
      subtitle={getFlatNameFromDevice(device)}
      chips={
        isTransferring ? (
          <ProductCardChip
            tone="info"
            label={t("layout.tiles.numberPorting.numberBeingTransferred")}
          />
        ) : (
          <ProductCardChip
            tone="warning"
            label={t("cockpit.filter.status-labels.onpPending")}
          />
        )
      }
      navigationTarget={
        missingInfo
          ? `${portalRoutes.manageDevice.numberPorting}?simId=${device.simId}`
          : `${manageRoute}?simId=${device.simId}`
      }
      buttonText={missingInfo ? t("layout.tiles.complete") : undefined}
      footer={
        <ProductCardFooterItem
          variant="list"
          title={tDefault("Onboarding.sections.number-porting.title")}
          steps={steps}
        />
      }
    />
  );
};
